// PreviewTester/components/RightPanel.js - UPDATED with Multi-Select Styles & Alignment

import React, {
  useEffect,
  useRef,
  forwardRef,
  useImperativeHandle,
  useState,
  useCallback,
} from "react";
import Controls from "../../PreviewEditorHome/Controls";
import PreviewContainer from "./PreviewContainer";
import PreviewInfo from "./PreviewInfo";
import ScenarioTree from "../../PreviewEditorHome/ScenarioTree";
import styles from "./RightPanel.module.css";
import {
  SELECTION_TYPES,
  getTotalOperations,
} from "../utils/transformationRules";
import {
  isButtonStep,
  isButtonGroupStep,
  isStyleStep,
  isStructuralStep,
  isMediaStep,
  isCombinedStep,
  hasOperation,
  hasMultipleOperations,
} from "../utils/transformationSchema";
import { IoClose } from "react-icons/io5";

const getCaseTypeLabel = (caseType) => {
  console.log("caseTypeddd", caseType);
  const labels = {
    case1: {
      label: "Multiple Screens",
      description:
        "Each item from your data appears as its own separate screen",
      icon: "📚",
      color: "#48bb78",
      bgColor: "#f0fff4",
    },
    case2: {
      label: "Single Screen",
      description: "All your data appears together on one screen",
      icon: "📄",
      color: "#4299e1",
      bgColor: "#ebf8ff",
    },
    case3: {
      label: "Multiple Screens",
      description:
        "Each item from your data appears as its own separate screen",
      icon: "📚",
      color: "#ed8936",
      bgColor: "#fffaf0",
    },
    unknown: {
      label: "Unknown",
      description: "Unable to detect layout type",
      icon: "❓",
      color: "#a0aec0",
      bgColor: "#f7fafc",
    },
  };
  return labels[caseType] || labels["unknown"];
};

// ============================================================
// ✅ UX: Phase information for the bubble (moved out of
// PreviewContainer so RightPanel can render it as a floating
// bubble attached to the phase chip's info icon).
// ============================================================
const PHASE_MESSAGES = {
  0: {
    title: "Pick an item to arrange",
    subtitle:
      "Turn one group of fields into a Table, Cards, List, or JSON view.",
    steps: [
      {
        n: 1,
        text: "Look at the preview below — one group is glowing orange.",
      },
      {
        n: 2,
        text: "Click and drag across that glowing area to select all its fields.",
      },
      {
        n: 3,
        text: "Choose a layout (Table, Cards, List, or JSON) from the popup.",
      },
    ],
    tip: "You can style or restyle any group later — nothing here is permanent.",
    color: "#2b6cb0",
    icon: "📐",
    arrowLabel: "Start by selecting the glowing area below",
  },
  1: {
    title: "Turn links into photos",
    subtitle: "Show real images instead of raw URLs.",
    steps: [
      {
        n: 1,
        text: "Find the blue underlined image link inside a card.",
      },
      {
        n: 2,
        text: "Click the link to replace it with the actual image.",
      },
      {
        n: 3,
        text: "Adjust the size and aspect ratio if needed.",
      },
    ],
    tip: "Each card's link becomes its own photo.",
    color: "#8b5e3c",
    icon: "🖼️",
    arrowLabel: "Click a blue image link below",
  },
  2: {
    title: "Make text stand out",
    subtitle: "Highlight, bold, italicise, or underline any text.",
    steps: [
      {
        n: 1,
        text: "Click and drag to select the exact text you want to style.",
      },
      {
        n: 2,
        text: "Pick one or more styles — they combine in one step.",
      },
      {
        n: 3,
        text: "Adjust alignment (left, center, right) if you want.",
      },
    ],
    tip: "You can apply multiple styles to the same selection.",
    color: "#38a169",
    icon: "🎨",
    arrowLabel: "Select any text below to begin",
  },
};

const RightPanel = forwardRef(
  (
    {
      renderedPreview,
      error,
      scrollMode,
      setScrollMode,
      layoutDirection,
      setLayoutDirection,
      dataType,
      parsedData,
      getScrollStyle,
      getPreviewContent,
      scenarioTree,
      renderScenarioTree,
      isSampleArray,
      arrayLayoutDirection,
      setArrayLayoutDirection,
      isControlsOpen,
      setIsControlsOpen,
      parsedSample,
      template,
      configId,
      kbId,
      statusCode,
      actions = [],
      onAddAction,
      onRemoveAction,
      transformationRules = [],
      // Selection & Transformation props
      isSelectingMode,
      setIsSelectingMode,
      selectedText,
      selectionType,
      showTransformationPanel,
      onApplyTransformation,
      onCloseTransformationPanel,
      onTextSelect,
      // Edit Mode props
      isEditMode = false,
      setIsEditMode = () => {},
      templateLines = [],
      setTemplateLines = () => {},
      availableVariables = [],
      onTemplateUpdate = () => {},
      onAddCustomMessage = () => {},
      // Phase Wizard props
      combinedStep = null,
      onStartPhaseWizard = () => {},
      onAddOperationToPhase = () => {},
      onRemoveOperationFromPhase = () => {},
      onNextPhase = () => {},
      onPrevPhase = () => {},
      onFinalizeCombinedStep = () => {},
      onCancelWizard = () => {},
      showPhaseWizard = false,
      isStructuralSelection = false,
      currentPhase = 0,
      hasPreviewData = false,
      // Selector Recalculation props
      onTrackChange = () => {},
      hasTransformations = false,
      // Tour props
      onSaveSuccess = () => {},
      // Modal mode props
      modalMode = false,
      onTemplateDataReady = null,
      // Case type for validation
      caseType = "unknown",
      queryId = { queryId },
      onOpenEditor = null,
      onCloseEditor = null,
      handleUpdateAction = null,
    },
    ref,
  ) => {
    const renderCountRef = useRef(0);
    const containerRef = useRef(null);
    const [hasTemplateChanges, setHasTemplateChanges] = useState(false);
    const [originalTemplate, setOriginalTemplate] = useState("");

    // Warning modal state
    const [showCloseEditorWarning, setShowCloseEditorWarning] = useState(false);
    const [pendingWizardStart, setPendingWizardStart] = useState(false);

    // ✅ UX: refs for outside-click detection on the Controls drawer
    const controlsPanelRef = useRef(null);
    const controlsToggleRef = useRef(null);

    // ============================================================
    // ✅ UX: Phase-info bubble state
    //
    // `phaseInfoBubbleState`:
    //   'closed'    → bubble fully unmounted
    //   'opening'   → mount + play "grow out of chip" animation
    //   'open'      → fully visible
    //   'closing'   → play "shrink into chip" animation, then unmount
    //
    // `phaseInfoTransitioningRef` guards against rapid toggles
    // (double-click on the info icon) while an animation is running.
    // ============================================================
    const [phaseInfoBubbleState, setPhaseInfoBubbleState] = useState("closed");
    const [phaseInfoBubbleVisible, setPhaseInfoBubbleVisible] = useState(false);
    const phaseInfoTransitioningRef = useRef(false);
    const phaseInfoTimersRef = useRef([]);

    // Track which phase's info icon is the anchor for the bubble.
    // We only render the bubble inside the ACTIVE phase chip, so
    // the anchor index equals `currentPhase`.
    const phaseInfoBubbleAnchorIndex = currentPhase;

    // Forward the ref to parent
    useImperativeHandle(ref, () => containerRef.current);

    // RightPanel.js - Add these states at the top level of the component
    const [imageWidth, setImageWidth] = useState(250);
    const [imageHeight, setImageHeight] = useState(250);
    const [preserveAspectRatio, setPreserveAspectRatio] = useState(true);

    // ✅ NEW: Add view mode state (desktop/mobile)
    const [viewMode, setViewMode] = useState("desktop"); // 'desktop' or 'mobile'

    // ✅ NEW: Style multi-select states
    const [selectedStyles, setSelectedStyles] = useState([]);
    const [alignment, setAlignment] = useState("left");
    const [isFullRowSelection, setIsFullRowSelection] = useState(false);
    const selectedStylesRef = useRef([]);

    // ============================================================
    // ✅ UX: Helpers to clear pending animation timers so rapid
    // toggles never stack up and freeze the browser.
    // ============================================================
    const clearPhaseInfoTimers = useCallback(() => {
      phaseInfoTimersRef.current.forEach((id) => clearTimeout(id));
      phaseInfoTimersRef.current = [];
    }, []);

    // Also add useEffect to load existing media dimensions
    useEffect(() => {
      if (combinedStep?.phases?.media?.operations) {
        const mediaOp = combinedStep.phases.media.operations.find(
          (o) => o.operation === "images",
        );
        if (mediaOp) {
          setImageWidth(mediaOp.imageWidth || 250);
          setImageHeight(mediaOp.imageHeight || 250);
          setPreserveAspectRatio(mediaOp.preserveAspectRatio !== false);
        }
      }
    }, [combinedStep]);

    // ✅ Load existing style operations into selectedStyles
    useEffect(() => {
      const styleOps = combinedStep?.phases?.styles?.operations || [];
      if (styleOps.length > 0) {
        // Find the most recent combined style operation
        const combinedOp = styleOps.find(
          (o) => o.operation === "style_combined",
        );
        if (
          combinedOp &&
          combinedOp.operations &&
          Array.isArray(combinedOp.operations)
        ) {
          setSelectedStyles(combinedOp.operations);
          selectedStylesRef.current = combinedOp.operations;
          if (combinedOp.style_meta?.alignment) {
            setAlignment(combinedOp.style_meta.alignment);
          }
          if (combinedOp.style_meta?.full_row !== undefined) {
            setIsFullRowSelection(combinedOp.style_meta.full_row);
          }
          return;
        }

        // Fallback: collect all unique style operations
        const allOps = styleOps
          .map((o) => o.operation)
          .filter((op) => op !== "style_combined");
        if (allOps.length > 0) {
          setSelectedStyles(allOps);
          selectedStylesRef.current = allOps;
        }
      } else {
        // Reset when operations are cleared
        setSelectedStyles([]);
        selectedStylesRef.current = [];
      }
    }, [combinedStep]);

    // ✅ Check if selection is a full row
    useEffect(() => {
      const isFullRow =
        selectionType?.isFullRow || selectionType?.isFullRowSelection || false;
      setIsFullRowSelection(isFullRow);
    }, [selectionType]);

    // Log when component renders
    renderCountRef.current += 1;
    console.log(`[RightPanel] 🔄 Render #${renderCountRef.current}`);
    console.log(`[RightPanel] 📊 Current dataType: "${dataType}"`);
    console.log(`[RightPanel] 📊 Is selecting mode: ${isSelectingMode}`);
    console.log(`[RightPanel] 📊 Show phase wizard: ${showPhaseWizard}`);
    console.log(`[RightPanel] 📊 Current phase: ${currentPhase}`);
    console.log(
      `[RightPanel] 📊 Selected text: ${selectedText ? selectedText.substring(0, 30) + "..." : "none"}`,
    );
    console.log(`[RightPanel] 📊 Has preview data: ${hasPreviewData}`);
    console.log(`[RightPanel] 📊 Case type: ${caseType}`);
    console.log(`[RightPanel] 📊 Selected styles:`, selectedStyles);

    console.log("reetuarrayLayoutDirection,", arrayLayoutDirection);

    // When template changes externally, update originalTemplate
    useEffect(() => {
      if (template && !isEditMode) {
        setOriginalTemplate(template);
      }
    }, [template, isEditMode]);

    // ============================================================
    // HANDLE TEMPLATE UPDATE FROM EDITOR
    // ============================================================
    const handleTemplateUpdate = (newLines) => {
      const newTemplate = newLines.map((line) => line.content).join("\n");
      console.log("[RightPanel] 📝 Template lines updated:", newTemplate);
      setHasTemplateChanges(newTemplate !== originalTemplate);
      if (onTemplateUpdate) {
        onTemplateUpdate(newTemplate);
      }
    };

    // Handle discard changes (revert to original)
    const handleDiscardChanges = () => {
      if (originalTemplate) {
        if (onTemplateUpdate) {
          onTemplateUpdate(originalTemplate);
        }
        setHasTemplateChanges(false);
      }
    };

    // Handle adding custom message
    const handleAddCustomMessage = () => {
      if (onAddCustomMessage) {
        onAddCustomMessage();
      }
    };

    // Handle save success - passes through to parent for tour completion
    const handleControlsSaveSuccess = (responseData) => {
      console.log(
        "[RightPanel] ✅ Save successful, triggering tour completion",
      );
      if (onSaveSuccess) {
        onSaveSuccess(responseData);
      }
    };

    // ============================================================
    // ✅ FIXED: Apply max-width to screens container based on viewMode
    // Uses MutationObserver to wait for the container to appear
    // ============================================================
    useEffect(() => {
      // Function to apply the view mode
      const applyViewMode = () => {
        // Try multiple selectors to find the screens container
        const screensContainer = document.querySelector(
          '.verticals, .horizontals, [class*="verticals"], [class*="horizontals"]',
        );

        if (screensContainer) {
          if (viewMode === "mobile") {
            screensContainer.style.maxWidth = "390px";
            screensContainer.style.margin = "0 auto";
            screensContainer.style.transition =
              "max-width 0.3s ease, margin 0.3s ease";
            screensContainer.style.border = "1px solid #e2e8f0";
            screensContainer.style.borderRadius = "8px";
            screensContainer.style.boxShadow = "0 2px 8px rgba(0,0,0,0.08)";
          } else {
            screensContainer.style.maxWidth = "100%";
            screensContainer.style.margin = "0";
            screensContainer.style.border = "none";
            screensContainer.style.borderRadius = "0";
            screensContainer.style.boxShadow = "none";
          }
          return true;
        }
        return false;
      };

      // Try immediately
      if (applyViewMode()) return;

      // If not found, set up a MutationObserver to wait for it
      const observer = new MutationObserver(() => {
        if (applyViewMode()) {
          observer.disconnect();
        }
      });

      observer.observe(document.body, {
        childList: true,
        subtree: true,
      });

      // Also try again after a short delay (for cases where DOM updates are batched)
      const timeoutId = setTimeout(() => {
        if (applyViewMode()) {
          observer.disconnect();
        }
      }, 500);

      // Cleanup
      return () => {
        observer.disconnect();
        clearTimeout(timeoutId);
      };
    }, [viewMode]);

    // ============================================================
    // DETECT CASE TYPE (fallback if not provided)
    // ============================================================
    const detectCaseType = useCallback(() => {
      // If caseType is passed from parent, use it
      if (caseType && caseType !== "unknown") {
        return caseType;
      }

      // Fallback: try to detect from template and parsedSample
      if (template && parsedSample) {
        const isArray = Array.isArray(parsedSample);
        const templateLines = template.split("\n");
        let hasContentBeforeLoop = false;
        let hasContentAfterLoop = false;
        let foundForLoop = false;
        let loopDepth = 0;
        let outerLoopEndLine = -1;

        for (let i = 0; i < templateLines.length; i++) {
          const trimmed = templateLines[i].trim();
          if (
            !trimmed ||
            trimmed.startsWith("{#") ||
            trimmed.startsWith("{% comment")
          )
            continue;

          const isForLoopStart = /{%\s*for\s+/.test(trimmed);
          const isForLoopEnd = /{%\s*endfor\s*%}/.test(trimmed);

          if (isForLoopStart) {
            foundForLoop = true;
            loopDepth++;
            continue;
          }
          if (isForLoopEnd) {
            loopDepth--;
            if (loopDepth === 0 && outerLoopEndLine === -1) {
              outerLoopEndLine = i;
            }
            continue;
          }

          if (!foundForLoop && loopDepth === 0) {
            const isTemplateTag =
              trimmed.startsWith("{%") || trimmed.startsWith("{#");
            if (!isTemplateTag && trimmed.length > 0) {
              hasContentBeforeLoop = true;
            }
          }

          if (
            foundForLoop &&
            loopDepth === 0 &&
            outerLoopEndLine !== -1 &&
            i > outerLoopEndLine
          ) {
            const isTemplateTag =
              trimmed.startsWith("{%") || trimmed.startsWith("{#");
            if (!isTemplateTag && trimmed.length > 0) {
              hasContentAfterLoop = true;
            }
          }
        }

        if (isArray) return "case1";
        if (foundForLoop && (hasContentBeforeLoop || hasContentAfterLoop))
          return "case2";
        if (foundForLoop && !hasContentBeforeLoop && !hasContentAfterLoop)
          return "case3";
        return "unknown";
      }

      return "unknown";
    }, [caseType, template, parsedSample]);

    // ============================================================
    // MUTUAL EXCLUSION: Toggle edit mode - closes wizard if opening
    // ============================================================
    // RightPanel.js - Update handleToggleEditMode
    const handleToggleEditMode = () => {
      if (!hasPreviewData) {
        console.log(
          "[RightPanel] ⏭️ Cannot toggle edit mode - no preview data",
        );
        return;
      }

      console.log(
        `[RightPanel] 🔘 Toggling edit mode: ${isEditMode ? "off" : "on"}`,
      );

      // If opening editor
      if (!isEditMode) {
        // Call the parent's open editor handler
        if (onOpenEditor) {
          onOpenEditor();
        } else {
          setIsEditMode(true);
        }
      } else {
        // Closing editor
        if (onCloseEditor) {
          onCloseEditor();
        } else {
          setIsEditMode(false);
        }
      }
    };

    // Log when any control state changes
    useEffect(() => {
      console.log(`[RightPanel] 🎛️ Controls state updated:`);
      console.log(`  - scrollMode: "${scrollMode}"`);
      console.log(`  - layoutDirection: "${layoutDirection}"`);
      console.log(`  - arrayLayoutDirection: "${arrayLayoutDirection}"`);
      console.log(`  - dataType: "${dataType}"`);
      console.log(`  - isSelectingMode: ${isSelectingMode}`);
      console.log(`  - isEditMode: ${isEditMode}`);
    }, [
      scrollMode,
      layoutDirection,
      arrayLayoutDirection,
      dataType,
      isSelectingMode,
      isEditMode,
    ]);

    // ============================================================
    // ✅ UX: Close the Controls drawer when clicking outside of it.
    // Ignores clicks on:
    //   • the panel itself
    //   • the toggle button
    //   • the transformation popup (which is rendered outside the panel)
    // ============================================================
    useEffect(() => {
      if (!isControlsOpen) return;

      const handleClickOutside = (event) => {
        const panelEl = controlsPanelRef.current;
        const toggleEl = controlsToggleRef.current;
        if (!panelEl) return;

        // Click inside panel → ignore
        if (panelEl.contains(event.target)) return;

        // Click on the toggle button → ignore (it handles its own toggle)
        if (toggleEl && toggleEl.contains(event.target)) return;

        // Click inside transformation popup → ignore
        const popupEl = document.getElementById("transformation-popup");
        if (popupEl && popupEl.contains(event.target)) return;

        setIsControlsOpen(false);
      };

      document.addEventListener("mousedown", handleClickOutside);
      return () =>
        document.removeEventListener("mousedown", handleClickOutside);
    }, [isControlsOpen, setIsControlsOpen]);

    // ============================================================
    // ✅ UX: Auto-open the phase-info bubble when the wizard is
    // active and the phase changes.
    // ============================================================
    useEffect(() => {
      if (!showPhaseWizard) {
        // Wizard closed → collapse the bubble immediately
        clearPhaseInfoTimers();
        phaseInfoTransitioningRef.current = false;
        setPhaseInfoBubbleState("closed");
        setPhaseInfoBubbleVisible(false);
        return;
      }

      // Wizard active → auto-open the bubble for the new phase
      clearPhaseInfoTimers();
      phaseInfoTransitioningRef.current = true;
      setPhaseInfoBubbleVisible(true);
      setPhaseInfoBubbleState("opening");

      const t = setTimeout(() => {
        setPhaseInfoBubbleState("open");
        phaseInfoTransitioningRef.current = false;
      }, 260); // matches animation duration
      phaseInfoTimersRef.current.push(t);
    }, [showPhaseWizard, currentPhase]);

    // Cleanup all pending timers on unmount
    useEffect(() => {
      return () => {
        clearPhaseInfoTimers();
      };
    }, [clearPhaseInfoTimers]);

    // ============================================================
    // ✅ UX: Toggle the phase-info bubble via the info icon.
    // Guarded against rapid re-entry (double-click protection).
    // ============================================================
    const handleTogglePhaseInfo = () => {
      // Guard: if an animation is in flight, ignore this click.
      if (phaseInfoTransitioningRef.current) return;

      // Current states:
      //   'closed'  → open
      //   'opening' → ignore (rare; guard above usually catches)
      //   'open'    → close
      //   'closing' → ignore (guard above usually catches)
      if (phaseInfoBubbleState === "closed") {
        phaseInfoTransitioningRef.current = true;
        clearPhaseInfoTimers();
        setPhaseInfoBubbleVisible(true);
        setPhaseInfoBubbleState("opening");

        const t = setTimeout(() => {
          setPhaseInfoBubbleState("open");
          phaseInfoTransitioningRef.current = false;
        }, 260);
        phaseInfoTimersRef.current.push(t);
      } else if (phaseInfoBubbleState === "open") {
        phaseInfoTransitioningRef.current = true;
        clearPhaseInfoTimers();
        setPhaseInfoBubbleState("closing");

        const t = setTimeout(() => {
          setPhaseInfoBubbleState("closed");
          setPhaseInfoBubbleVisible(false);
          phaseInfoTransitioningRef.current = false;
        }, 220);
        phaseInfoTimersRef.current.push(t);
      }
      // If 'opening' or 'closing' → do nothing (guard handles it)
    };

    // RightPanel.js - Update getPreviewContentWithLogging
    // RightPanel.js - Update getPreviewContentWithLogging
    const getPreviewContentWithLogging = () => {
      console.log(`[RightPanel] 📝 getPreviewContent called`);
      const result = getPreviewContent();

      // ✅ Check if we already have a structural transformation applied
      const hasStructuralTransformation =
        combinedStep?.phases?.structural?.operations?.length > 0;

      // ✅ Add border to first screen and apply loop highlighting when in wizard mode
      if (showPhaseWizard) {
        setTimeout(() => {
          const screens = document.querySelectorAll(".item-ui-screen");
          const screenCount = screens.length;

          if (screenCount > 1) {
            screens.forEach((screen, index) => {
              if (index === 0) {
                // ✅ Just add a simple border to the first screen
                screen.style.border = "10px solid #4a7feb";
                screen.style.borderRadius = "0px";
                screen.style.boxShadow = "rgba(0, 0, 0, 0.35) 0px 5px 15px";
              } else {
                // Other screens get a subtle dimming
                // screen.style.opacity = "0.7";
              }
            });
          }

          // ============================================================
          // ✅ NEW: Apply / Remove the Light Orange Loop Background
          // ============================================================
          const firstScreen =
            document.querySelector('.item-ui-screen[data-item-ui-index="0"]') ||
            screens[0];

          if (firstScreen) {
            const loopContainers =
              firstScreen.querySelectorAll(".for-loop-started");

            loopContainers.forEach((loop) => {
              if (showPhaseWizard && !hasStructuralTransformation) {
                // ✅ Add class ONLY if wizard is active and NO transformation is applied yet
                loop.classList.add("loop-phase-active");
              } else {
                // ✅ Remove class if transformation is already applied (or wizard is off)
                loop.classList.remove("loop-phase-active");
              }
            });
          }
          // ============================================================
        }, 100);
      } else {
        // If wizard is closed, ensure we remove the classes
        setTimeout(() => {
          const allLoops = document.querySelectorAll(".for-loop-started");
          allLoops.forEach((loop) => {
            loop.classList.remove("loop-phase-active");
          });
        }, 100);
      }

      console.log(
        `[RightPanel] 📝 getPreviewContent returned, renderedPreview length: ${renderedPreview?.length || 0}`,
      );
      return result;
    };

    // Determine if there's parsed data to display controls for
    const hasParsedData =
      parsedSample &&
      (typeof parsedSample === "object" || Array.isArray(parsedSample));
    console.log(`[RightPanel] 📊 hasParsedData: ${hasParsedData}`);

    // Phase info
    const phaseInfo = [
      {
        id: "structural",
        label: "📐 Structural",
        description: "Change HTML structure",
        color: "#2b6cb0",
        bgColor: "#ebf8ff",
      },
      {
        id: "media",
        label: "🖼️ Media",
        description: "Convert images",
        color: "#8b5e3c",
        bgColor: "#faf0e6",
      },
      {
        id: "styles",
        label: "🎨 Style",
        description: "Apply CSS styles",
        color: "#38a169",
        bgColor: "#f0fff4",
        className: "styles-phase",
      },
    ];

    const currentPhaseInfo = phaseInfo[currentPhase] || phaseInfo[0];

    // ============================================================
    // GET SELECTION INFO
    // ============================================================

    const getSelectionInfo = () => {
      if (!selectionType) return null;

      const hasImageUrls =
        selectedText &&
        (selectedText.includes(".jpg") ||
          selectedText.includes(".jpeg") ||
          selectedText.includes(".png") ||
          selectedText.includes(".gif") ||
          selectedText.includes(".webp") ||
          selectedText.includes("images.pexels.com") ||
          selectedText.includes("unsplash.com") ||
          selectedText.includes("imgur.com"));

      const hasImageUrlClass = selectionType?.hasImageUrlClass || false;

      switch (selectionType.type) {
        case SELECTION_TYPES.STRUCTURAL_MULTIPLE:
          return {
            label: `${selectionType.count} items (Structural)`,
            icon: "📐",
            description: "Convert multiple items to a structured view",
            hasImages: hasImageUrls || hasImageUrlClass,
            canDoStructural: true,
            isFullRow: selectionType.isFullRow || false,
          };
        case SELECTION_TYPES.STYLE_MULTIPLE:
          return {
            label: `${selectionType.count} fields (Style)`,
            icon: "🎨",
            description: "Apply style to all selected fields",
            hasImages: hasImageUrls || hasImageUrlClass,
            canDoStructural: false,
            isFullRow: selectionType.isFullRow || false,
          };
        case SELECTION_TYPES.SINGLE:
          return {
            label: "1 field (Style)",
            icon: "🎨",
            description: "Apply style to the selected field",
            hasImages: hasImageUrls || hasImageUrlClass,
            canDoStructural: false,
            isFullRow: selectionType.isFullRow || false,
          };
        default:
          return {
            label: "text",
            icon: "📝",
            description: "Apply transformation to selected text",
            hasImages: hasImageUrls || hasImageUrlClass,
            canDoStructural: false,
            isFullRow: false,
          };
      }
    };

    const selectionInfo = getSelectionInfo();

    // ============================================================
    // AUTO-CLOSE EDITOR ON NEXT/COMPLETE
    // ============================================================
    // RightPanel.js - Update the handleNextPhaseWithAutoClose function

    // RightPanel.js - Updated handleNextPhaseWithAutoClose

    const handleNextPhaseWithAutoClose = () => {
      console.log("[RightPanel] ➡️ Next phase clicked, auto-closing editor");

      if (isEditMode) {
        setIsEditMode(false);
      }

      // ✅ Check if we're on the Structural phase and Media has no images
      const hasImageUrlsInPreview =
        renderedPreview &&
        (renderedPreview.includes('class="value image-url"') ||
          renderedPreview.includes("data-image-url") ||
          renderedPreview.includes(".jpg") ||
          renderedPreview.includes(".png") ||
          renderedPreview.includes(".jpeg") ||
          renderedPreview.includes("images.pexels.com") ||
          renderedPreview.includes("unsplash.com") ||
          renderedPreview.includes("imgur.com"));

      // ✅ If we're on Structural (phase 0) and no images, skip directly to Styles (phase 2)
      if (currentPhase === 0 && !hasImageUrlsInPreview) {
        console.log(
          "[RightPanel] ⏭️ Skipping Media phase (no images available) - going directly to Styles",
        );
        // Call onNextPhase twice to skip Media
        if (onNextPhase) {
          onNextPhase(); // Go to Media (phase 1)
          // Immediately call again to go to Styles (phase 2)
          // Use setTimeout to ensure state updates
          setTimeout(() => {
            if (onNextPhase) {
              onNextPhase(); // Go to Styles (phase 2)
            }
          }, 50);
        }
        return;
      }

      // Normal next phase flow (one step at a time)
      if (onNextPhase) {
        onNextPhase();
      }
    };

    const handleCompleteWithAutoClose = () => {
      console.log(
        "[RightPanel] ✅ Complete Wizard clicked, auto-closing editor",
      );
      if (isEditMode) {
        setIsEditMode(false);
      }
      if (onFinalizeCombinedStep) {
        onFinalizeCombinedStep();
      }
    };

    // ============================================================
    // ✅ UPDATED: extractButtonsFromLines - Better handling of new format
    // ============================================================
    const extractButtonsFromLines = (lines) => {
      console.log("[🔘 BUTTON FLOW] extractButtonsFromLines START");
      console.log("[🔘 BUTTON FLOW] lines length:", lines?.length);

      if (!lines || !Array.isArray(lines)) {
        console.log(
          "[🔘 BUTTON FLOW] lines is null or not an array, returning []",
        );
        return [];
      }

      console.log("[🔘 BUTTON FLOW] lines rows:");
      lines.forEach((line, i) => {
        console.log(
          `  [${i}] type: ${line.type}, content: "${line.content?.substring(0, 30)}", isButton: ${line.isButton}, buttonText: ${line.buttonText}`,
        );
      });

      const buttons = [];

      for (const line of lines) {
        // ✅ Check for regular button - using schema helper
        if (line.type === "button" || line.isButton === true) {
          console.log(
            `[🔘 BUTTON FLOW] Found regular button: "${line.buttonText || line.content}"`,
          );
          buttons.push({
            text: line.buttonText || line.content,
            id: line.id,
            type: "button",
          });
          continue;
        }

        // ✅ Check for button group - extract all buttons from the group
        // Now handles both string and object formats
        if (
          line.type === "button-group" &&
          line.buttons &&
          Array.isArray(line.buttons)
        ) {
          console.log(
            `[🔘 BUTTON FLOW] Found button group: "${line.content}" with ${line.buttons.length} buttons`,
          );

          // ✅ Handle both string buttons (new format) and object buttons (old format)
          const buttonTexts = line.buttons.map((btn) => {
            if (typeof btn === "string") {
              return btn; // New format: buttons are strings
            }
            return btn.text || btn.label || String(btn); // Old format: buttons are objects
          });

          // ✅ Also check if buttons are stored as strings in the group (new format)
          // Some groups might have buttons as strings but we already handled that above

          for (const btn of buttonTexts) {
            console.log(`[🔘 BUTTON FLOW]   - Extracted button: "${btn}"`);
            buttons.push({
              text: btn,
              id: `btn-${line.id}-${buttons.length}`,
              groupId: line.id,
              isFromGroup: true,
              type: "button-group-button",
            });
          }
          continue;
        }

        // Check by content pattern (existing logic - fallback)
        if (line.content) {
          if (
            line.content.includes('class="row button"') ||
            line.content.includes("class='row button'")
          ) {
            const match = line.content.match(/<span[^>]*>([^<]*)<\/span>/);
            if (match) {
              console.log(
                `[🔘 BUTTON FLOW] Found button by HTML pattern: "${match[1]}"`,
              );
              buttons.push({
                text: match[1].trim(),
                id: line.id,
                type: "button",
              });
              continue;
            }
          }

          if (line.isButton === true) {
            console.log(
              `[🔘 BUTTON FLOW] Found button by isButton flag: "${line.buttonText || line.content}"`,
            );
            buttons.push({
              text: line.buttonText || line.content,
              id: line.id,
              type: "button",
            });
          }
        }
      }

      console.log("[🔘 BUTTON FLOW] extractButtonsFromLines result:", buttons);
      console.log("[🔘 BUTTON FLOW] extractButtonsFromLines END");
      return buttons;
    };

    // Then pass to Controls
    const buttons = extractButtonsFromLines(templateLines);

    // ============================================================
    // ✅ UPDATED: renderTransformationPopup - With Multi-Select Styles & Alignment
    // ============================================================
    // RightPanel.js - Full renderTransformationPopup

    const renderTransformationPopup = () => {
      if (!showPhaseWizard || !selectedText || !selectedText.trim()) {
        console.log("[RightPanel] ⏭️ Popup conditions not met:", {
          showPhaseWizard,
          hasSelectedText: !!selectedText,
          selectedTextLength: selectedText?.length || 0,
          showTransformationPanel,
        });
        return null;
      }

      if (!showTransformationPanel) {
        console.log("[RightPanel] ⏭️ Transformation panel not shown");
        return null;
      }

      console.log(
        "[RightPanel] ✅ Rendering transformation popup for phase:",
        currentPhaseInfo.label,
      );

      const phaseOperations = {
        structural: [
          {
            id: "table",
            label: "📊 Table View",
            color: "#2b6cb0",
            bgColor: "#ebf8ff",
          },
          {
            id: "cards",
            label: "🃏 Card View",
            color: "#276749",
            bgColor: "#f0fff4",
          },
          {
            id: "json",
            label: "📋 JSON View",
            color: "#744210",
            bgColor: "#fefcbf",
          },
          {
            id: "list",
            label: "📝 List View",
            color: "#553c9a",
            bgColor: "#faf5ff",
          },
        ],
        media: [
          {
            id: "images",
            label: "🖼️ Render Images",
            color: "#8b5e3c",
            bgColor: "#faf0e6",
          },
        ],
        styles: [
          {
            id: "highlight",
            label: "✨ Highlight",
            color: "#d69e2e",
            bgColor: "#fefcbf",
          },
          { id: "bold", label: "𝐁 Bold", color: "#2d3748", bgColor: "#edf2f7" },
          {
            id: "italic",
            label: "𝐼 Italic",
            color: "#2d3748",
            bgColor: "#edf2f7",
          },
          {
            id: "underline",
            label: "U̲ Underline",
            color: "#2d3748",
            bgColor: "#edf2f7",
          },
          {
            id: "strikethrough",
            label: "S̶t̶r̶i̶k̶e̶",
            color: "#2d3748",
            bgColor: "#edf2f7",
          },
        ],
      };

      const currentOps = phaseOperations[currentPhaseInfo.id] || [];
      const phaseName = currentPhaseInfo.id;
      const canApplyStructural =
        currentPhaseInfo.id === "structural" &&
        (selectionType?.isStructural || isStructuralSelection);

      // ✅ Check if there are image URLs in the selection
      const hasImageUrlsInSelection =
        selectedText &&
        (selectedText.includes(".jpg") ||
          selectedText.includes(".jpeg") ||
          selectedText.includes(".png") ||
          selectedText.includes(".gif") ||
          selectedText.includes(".webp") ||
          selectedText.includes("images.pexels.com") ||
          selectedText.includes("unsplash.com") ||
          selectedText.includes("imgur.com"));

      // ✅ Check if there are image URLs in the preview
      const hasImageUrlsInPreview =
        renderedPreview &&
        (renderedPreview.includes('class="value image-url"') ||
          renderedPreview.includes("data-image-url") ||
          renderedPreview.includes(".jpg") ||
          renderedPreview.includes(".png") ||
          renderedPreview.includes(".jpeg") ||
          renderedPreview.includes("images.pexels.com") ||
          renderedPreview.includes("unsplash.com") ||
          renderedPreview.includes("imgur.com"));

      // ✅ Combined check for media availability
      const canApplyMedia = hasImageUrlsInSelection || hasImageUrlsInPreview;

      // ✅ Get selectors from combinedStep
      const currentSelectors = combinedStep?.selectors || [];

      // ✅ Check if this is a full row selection
      const isFullRow = selectionInfo?.isFullRow || false;

      // ✅ Alignment options
      const alignmentOptions = [
        { id: "left", label: "⬅️ Left" },
        { id: "center", label: "⬛ Center" },
        { id: "right", label: "➡️ Right" },
      ];

      // ✅ Toggle style function
      const toggleStyle = (styleId) => {
        const current = selectedStylesRef.current || [];
        if (current.includes(styleId)) {
          selectedStylesRef.current = current.filter((s) => s !== styleId);
        } else {
          selectedStylesRef.current = [...current, styleId];
        }
        setSelectedStyles([...selectedStylesRef.current]);
      };

      // ✅ Apply styles function
      const handleApplyStyles = () => {
        const stylesToApply = selectedStylesRef.current || [];
        if (stylesToApply.length === 0) {
          alert("Please select at least one style");
          return;
        }

        const style_meta = {
          full_row: isFullRow,
          alignment: alignment,
        };

        // Add the combined style operation
        onAddOperationToPhase("styles", {
          operation: "style_combined",
          operations: stylesToApply,
          style_meta: style_meta,
          fields: selectionType?.keys || [],
          selectors: currentSelectors,
        });

        // Clear selection after adding
        selectedStylesRef.current = [];
        setSelectedStyles([]);
      };

      return (
        <div
          style={{
            position: "fixed",
            bottom: "30px",
            left: "50%",
            transform: "translateX(-50%)",
            backgroundColor: "white",
            borderRadius: "12px",
            padding: "16px 20px",
            boxShadow: "0 10px 40px rgba(0,0,0,0.25)",
            zIndex: 2000,
            maxWidth: "600px",
            width: "90%",
            border: `2px solid ${currentPhaseInfo.color}`,
            animation: "slideUp 0.3s ease",
            maxHeight: "80vh",
            overflowY: "auto",
          }}
          className="style-popups"
        >
          <style>
            {`
        @keyframes slideUp {
          from { opacity: 0; transform: translateX(-50%) translateY(20px); }
          to { opacity: 1; transform: translateX(-50%) translateY(0); }
        }
      `}
          </style>

          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              marginBottom: "10px",
            }}
          >
            <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
              <span
                style={{
                  fontWeight: "600",
                  fontSize: "13px",
                  color: currentPhaseInfo.color,
                }}
              >
                {currentPhaseInfo.label}
              </span>
              <span
                style={{
                  fontSize: "10px",
                  color: "#718096",
                  backgroundColor: "#f7fafc",
                  padding: "1px 8px",
                  borderRadius: "10px",
                }}
              >
                {currentPhaseInfo.description}
              </span>
            </div>
            <button
              onClick={() => {
                console.log("[RightPanel] ❌ Closing transformation popup");
                onCloseTransformationPanel && onCloseTransformationPanel();
              }}
              style={{
                background: "transparent",
                border: "none",
                fontSize: "16px",
                cursor: "pointer",
                color: "#a0aec0",
              }}
            >
              ✕
            </button>
          </div>

          <div
            style={{
              fontSize: "11px",
              color: "#718096",
              marginBottom: "10px",
              padding: "4px 8px",
              backgroundColor: "#f7fafc",
              borderRadius: "4px",
              display: "flex",
              alignItems: "center",
              gap: "8px",
            }}
          >
            <span style={{ fontWeight: "500" }}>Selected:</span>
            <span
              style={{
                fontStyle: "italic",
                flex: 1,
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
              }}
            >
              "{selectedText.substring(0, 50)}
              {selectedText.length > 50 ? "..." : ""}"
            </span>
            {selectionInfo && (
              <span
                style={{
                  fontSize: "9px",
                  padding: "1px 8px",
                  borderRadius: "10px",
                  backgroundColor: "#edf2f7",
                  color: "#4a5568",
                }}
              >
                {selectionInfo.icon} {selectionInfo.label}
              </span>
            )}
            {isFullRow && (
              <span
                style={{
                  fontSize: "9px",
                  padding: "1px 8px",
                  borderRadius: "10px",
                  backgroundColor: "#f0fff4",
                  color: "#48bb78",
                }}
              >
                📏 Full Row
              </span>
            )}
          </div>

          {/* ============================================================
          MEDIA PHASE - With "No Images" handling
          ============================================================ */}
          {phaseName === "media" && (
            <div>
              {/* Status message */}
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: "8px",
                  padding: "8px 12px",
                  marginBottom: "12px",
                  backgroundColor: canApplyMedia ? "#f0fff4" : "#fefcbf",
                  border: `1px solid ${canApplyMedia ? "#48bb78" : "#f6ad55"}`,
                  borderRadius: "6px",
                  fontSize: "12px",
                  color: canApplyMedia ? "#22543d" : "#744210",
                }}
              >
                <span style={{ fontSize: "16px" }}>
                  {canApplyMedia ? "✅" : "ℹ️"}
                </span>
                <span>
                  {canApplyMedia
                    ? "Image URLs detected! Select an image URL to convert it to a rendered image."
                    : "No image URLs found in your data. You can skip this step and proceed to styling."}
                </span>
              </div>

              {/* Image Size Controls - Only show if images are available in selection */}
              {canApplyMedia && hasImageUrlsInSelection && (
                <div
                  style={{
                    display: "flex",
                    flexDirection: "column",
                    gap: "8px",
                    padding: "10px 12px",
                    backgroundColor: "#f7fafc",
                    borderRadius: "6px",
                    border: "1px solid #e2e8f0",
                    marginBottom: "12px",
                  }}
                >
                  <div
                    style={{
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "center",
                    }}
                  >
                    <span
                      style={{
                        fontSize: "12px",
                        fontWeight: "600",
                        color: "#4a5568",
                      }}
                    >
                      📐 Image Size
                    </span>
                    <label
                      style={{
                        display: "flex",
                        alignItems: "center",
                        gap: "6px",
                        fontSize: "11px",
                        color: "#718096",
                        cursor: "pointer",
                      }}
                    >
                      <input
                        type="checkbox"
                        checked={preserveAspectRatio}
                        onChange={(e) =>
                          setPreserveAspectRatio(e.target.checked)
                        }
                        style={{ cursor: "pointer" }}
                      />
                      Preserve Aspect Ratio
                    </label>
                  </div>

                  <div
                    style={{
                      display: "flex",
                      gap: "16px",
                      alignItems: "center",
                    }}
                  >
                    <div style={{ flex: 1 }}>
                      <label
                        style={{
                          display: "block",
                          fontSize: "10px",
                          color: "#718096",
                          marginBottom: "2px",
                        }}
                      >
                        Width (px)
                      </label>
                      <input
                        type="number"
                        value={imageWidth}
                        onChange={(e) => {
                          const val = parseInt(e.target.value) || 100;
                          setImageWidth(val);
                          if (preserveAspectRatio) {
                            setImageHeight(val);
                          }
                        }}
                        min="50"
                        max="800"
                        step="10"
                        style={{
                          width: "100%",
                          padding: "4px 8px",
                          border: "1px solid #e2e8f0",
                          borderRadius: "4px",
                          fontSize: "12px",
                        }}
                      />
                    </div>

                    <span
                      style={{
                        color: "#a0aec0",
                        fontSize: "14px",
                        fontWeight: "300",
                      }}
                    >
                      ×
                    </span>

                    <div style={{ flex: 1 }}>
                      <label
                        style={{
                          display: "block",
                          fontSize: "10px",
                          color: "#718096",
                          marginBottom: "2px",
                        }}
                      >
                        Height (px)
                      </label>
                      <input
                        type="number"
                        value={imageHeight}
                        onChange={(e) => {
                          const val = parseInt(e.target.value) || 100;
                          setImageHeight(val);
                          if (preserveAspectRatio) {
                            setImageWidth(val);
                          }
                        }}
                        min="50"
                        max="800"
                        step="10"
                        style={{
                          width: "100%",
                          padding: "4px 8px",
                          border: "1px solid #e2e8f0",
                          borderRadius: "4px",
                          fontSize: "12px",
                        }}
                        disabled={preserveAspectRatio}
                      />
                    </div>
                  </div>

                  <div
                    style={{
                      fontSize: "10px",
                      color: "#a0aec0",
                      fontStyle: "italic",
                    }}
                  >
                    {preserveAspectRatio
                      ? "🔒 Aspect ratio locked"
                      : "🔓 Free resize"}
                  </div>
                </div>
              )}

              {/* Media operation buttons - Only show if images are available */}
              {canApplyMedia && (
                <div
                  style={{
                    display: "flex",
                    flexWrap: "wrap",
                    gap: "6px",
                    marginBottom: "12px",
                  }}
                >
                  {currentOps.map((op) => {
                    const disabled = !hasImageUrlsInSelection;
                    const tooltip = !hasImageUrlsInSelection
                      ? "Select text containing image URLs"
                      : "";

                    const phaseOps =
                      combinedStep?.phases?.[phaseName]?.operations || [];
                    const isActive = phaseOps.some(
                      (o) => o.operation === op.id,
                    );

                    return (
                      <button
                        key={op.id}
                        onClick={() => {
                          if (!disabled) {
                            console.log(
                              `[RightPanel] ➕ Adding ${op.id} to ${phaseName} phase`,
                            );

                            const operationData = {
                              operation: op.id,
                              selectionType,
                              hasImageUrls: hasImageUrlsInSelection,
                              selectors: currentSelectors,
                            };

                            if (phaseName === "media" && op.id === "images") {
                              operationData.imageWidth = imageWidth;
                              operationData.imageHeight = imageHeight;
                              operationData.preserveAspectRatio =
                                preserveAspectRatio;
                              console.log(
                                `[RightPanel] 🖼️ Adding media with dimensions: ${imageWidth}×${imageHeight}`,
                              );
                            }

                            onAddOperationToPhase(phaseName, operationData);
                          }
                        }}
                        disabled={disabled}
                        style={{
                          padding: "5px 12px",
                          backgroundColor: isActive
                            ? op.color
                            : disabled
                              ? "#f7fafc"
                              : op.bgColor,
                          color: isActive
                            ? "white"
                            : disabled
                              ? "#a0aec0"
                              : op.color,
                          border: `1px solid ${isActive ? op.color : disabled ? "#e2e8f0" : op.color}`,
                          borderRadius: "4px",
                          cursor: disabled ? "not-allowed" : "pointer",
                          fontSize: "11px",
                          fontWeight: isActive ? "600" : "500",
                          opacity: disabled ? 0.6 : 1,
                          transition: "all 0.2s",
                        }}
                        title={tooltip}
                      >
                        {isActive && "✓ "}
                        {op.label}
                      </button>
                    );
                  })}
                </div>
              )}

              {/* Show message when no images available */}
              {!canApplyMedia && (
                <div
                  style={{
                    padding: "12px 16px",
                    backgroundColor: "#f7fafc",
                    borderRadius: "6px",
                    border: "1px solid #e2e8f0",
                    textAlign: "center",
                    marginBottom: "12px",
                  }}
                >
                  <div
                    style={{
                      fontSize: "13px",
                      color: "#4a5568",
                      marginBottom: "4px",
                    }}
                  >
                    📷 No images to display
                  </div>
                  <div
                    style={{
                      fontSize: "11px",
                      color: "#718096",
                    }}
                  >
                    Your current data doesn't contain any image URLs. You can
                    proceed to the next step.
                  </div>
                </div>
              )}

              {/* Show current dimensions if media is active */}
              {canApplyMedia &&
                combinedStep?.phases?.media?.operations?.some(
                  (o) => o.operation === "images",
                ) && (
                  <div
                    style={{
                      fontSize: "10px",
                      color: "#718096",
                      textAlign: "center",
                      padding: "4px 8px",
                      backgroundColor: "#f0faf0",
                      borderRadius: "4px",
                      border: "1px solid #c6f6d5",
                    }}
                  >
                    ✅ Images will be rendered at {imageWidth}×{imageHeight}px
                    {preserveAspectRatio && " (aspect ratio preserved)"}
                  </div>
                )}

              {/* Skip button when no images available */}
              {!canApplyMedia && (
                <button
                  onClick={handleNextPhaseWithAutoClose}
                  style={{
                    width: "100%",
                    padding: "8px",
                    backgroundColor: "#edf2f7",
                    color: "#4a5568",
                    border: "1px solid #e2e8f0",
                    borderRadius: "4px",
                    cursor: "pointer",
                    fontSize: "12px",
                    fontWeight: "500",
                    transition: "all 0.2s",
                    marginTop: "8px",
                  }}
                  onMouseEnter={(e) => {
                    e.target.style.backgroundColor = "#e2e8f0";
                  }}
                  onMouseLeave={(e) => {
                    e.target.style.backgroundColor = "#edf2f7";
                  }}
                >
                  Skip Media →
                </button>
              )}
            </div>
          )}

          {/* ============================================================
          STRUCTURAL PHASE
          ============================================================ */}
          {phaseName === "structural" && (
            <div
              style={{
                display: "flex",
                flexWrap: "wrap",
                gap: "6px",
                marginBottom: "12px",
              }}
            >
              {currentOps.map((op) => {
                // ✅ Only enable in structural phase
                const isStructuralPhase = currentPhaseInfo.id === "structural";
                const disabled = !isStructuralPhase || !canApplyStructural;
                const tooltip = !canApplyStructural
                  ? "Select multiple fields to enable structural transformations"
                  : "";

                const phaseOps =
                  combinedStep?.phases?.[phaseName]?.operations || [];
                const isActive = phaseOps.some((o) => o.operation === op.id);

                return (
                  <button
                    key={op.id}
                    onClick={() => {
                      if (!disabled) {
                        console.log(
                          `[RightPanel] ➕ Adding ${op.id} to ${phaseName} phase`,
                        );

                        const operationData = {
                          operation: op.id,
                          selectionType,
                          selectors: currentSelectors,
                        };

                        if (phaseName === "structural") {
                          operationData.fields = selectionType?.keys || [];
                        }

                        onAddOperationToPhase(phaseName, operationData);
                      }
                    }}
                    disabled={disabled}
                    style={{
                      padding: "5px 12px",
                      backgroundColor: isActive
                        ? op.color
                        : disabled
                          ? "#f7fafc"
                          : op.bgColor,
                      color: isActive
                        ? "white"
                        : disabled
                          ? "#a0aec0"
                          : op.color,
                      border: `1px solid ${isActive ? op.color : disabled ? "#e2e8f0" : op.color}`,
                      borderRadius: "4px",
                      cursor: disabled ? "not-allowed" : "pointer",
                      fontSize: "11px",
                      fontWeight: isActive ? "600" : "500",
                      opacity: disabled ? 0.6 : 1,
                      transition: "all 0.2s",
                    }}
                    title={tooltip}
                  >
                    {isActive && "✓ "}
                    {op.label}
                  </button>
                );
              })}
            </div>
          )}

          {/* ============================================================
          STYLES PHASE - Multi-select with checkboxes and alignment
          ============================================================ */}
          {phaseName === "styles" && (
            <div>
              <div
                style={{
                  fontSize: "12px",
                  color: "#4a5568",
                  marginBottom: "10px",
                  padding: "6px 10px",
                  backgroundColor: "#f7fafc",
                  borderRadius: "4px",
                }}
              >
                <strong>Select one or more styles</strong> to apply to the
                selected content.
              </div>

              {/* Style buttons - toggle mode */}
              <div
                style={{
                  display: "flex",
                  flexWrap: "wrap",
                  gap: "6px",
                  marginBottom: "12px",
                }}
              >
                {currentOps.map((op) => {
                  const isActive = (selectedStylesRef.current || []).includes(
                    op.id,
                  );

                  const phaseOps =
                    combinedStep?.phases?.styles?.operations || [];
                  const isApplied = phaseOps.some((o) => {
                    if (o.operation === "style_combined" && o.operations) {
                      return o.operations.includes(op.id);
                    }
                    return o.operation === op.id;
                  });

                  return (
                    <button
                      key={op.id}
                      onClick={() => toggleStyle(op.id)}
                      style={{
                        padding: "6px 14px",
                        backgroundColor: isActive
                          ? op.color
                          : isApplied
                            ? op.bgColor
                            : "white",
                        color: isActive ? "white" : op.color,
                        border: `2px solid ${isActive ? op.color : isApplied ? op.color : "#e2e8f0"}`,
                        borderRadius: "4px",
                        cursor: "pointer",
                        fontSize: "12px",
                        fontWeight: isActive ? "600" : "500",
                        transition: "all 0.2s",
                        boxShadow: isActive
                          ? "0 2px 4px rgba(0,0,0,0.1)"
                          : "none",
                        opacity: isApplied && !isActive ? 0.7 : 1,
                      }}
                      title={
                        isApplied && !isActive
                          ? "Already applied, click to toggle"
                          : ""
                      }
                    >
                      {isActive && "✓ "}
                      {op.label}
                      {isApplied && !isActive && " (applied)"}
                    </button>
                  );
                })}
              </div>

              {/* Selected styles count */}
              {(selectedStylesRef.current || []).length > 0 && (
                <div
                  style={{
                    fontSize: "11px",
                    color: "#4a5568",
                    padding: "4px 10px",
                    backgroundColor: "#edf2f7",
                    borderRadius: "4px",
                    marginBottom: "12px",
                    display: "flex",
                    alignItems: "center",
                    gap: "8px",
                    flexWrap: "wrap",
                  }}
                >
                  <span>📌 Selected:</span>
                  <span style={{ fontWeight: "500" }}>
                    {(selectedStylesRef.current || []).join(", ")}
                  </span>
                  <button
                    onClick={() => {
                      selectedStylesRef.current = [];
                      setSelectedStyles([]);
                    }}
                    style={{
                      padding: "0 8px",
                      backgroundColor: "transparent",
                      border: "none",
                      color: "#fc8181",
                      cursor: "pointer",
                      fontSize: "11px",
                    }}
                  >
                    Clear all
                  </button>
                </div>
              )}

              {/* ✅ Alignment options - ALWAYS shown for ANY selection */}
              <div
                style={{
                  marginBottom: "12px",
                  padding: "10px 12px",
                  backgroundColor: "#f0f4ff",
                  borderRadius: "6px",
                  border: "1px solid #bee3f8",
                }}
              >
                <div
                  style={{
                    fontSize: "12px",
                    fontWeight: "500",
                    color: "#2b6cb0",
                    marginBottom: "6px",
                  }}
                >
                  📐 Text Alignment
                </div>
                <div
                  style={{
                    display: "flex",
                    gap: "8px",
                    flexWrap: "wrap",
                  }}
                >
                  {alignmentOptions.map((opt) => (
                    <button
                      key={opt.id}
                      onClick={() => setAlignment(opt.id)}
                      style={{
                        padding: "4px 14px",
                        backgroundColor:
                          alignment === opt.id ? "#4299e1" : "white",
                        color: alignment === opt.id ? "white" : "#4a5568",
                        border: `2px solid ${alignment === opt.id ? "#4299e1" : "#e2e8f0"}`,
                        borderRadius: "4px",
                        cursor: "pointer",
                        fontSize: "12px",
                        fontWeight: alignment === opt.id ? "600" : "400",
                        transition: "all 0.2s",
                      }}
                    >
                      {opt.label}
                    </button>
                  ))}
                </div>
              </div>

              {/* Apply styles button */}
              <button
                onClick={handleApplyStyles}
                disabled={(selectedStylesRef.current || []).length === 0}
                style={{
                  width: "100%",
                  padding: "8px",
                  backgroundColor:
                    (selectedStylesRef.current || []).length > 0
                      ? "#48bb78"
                      : "#e2e8f0",
                  color:
                    (selectedStylesRef.current || []).length > 0
                      ? "white"
                      : "#a0aec0",
                  border: "none",
                  borderRadius: "4px",
                  cursor:
                    (selectedStylesRef.current || []).length > 0
                      ? "pointer"
                      : "not-allowed",
                  fontSize: "13px",
                  fontWeight: "600",
                  transition: "background-color 0.2s",
                }}
              >
                {(selectedStylesRef.current || []).length > 0
                  ? `✓ Apply ${(selectedStylesRef.current || []).length} Style${(selectedStylesRef.current || []).length > 1 ? "s" : ""}`
                  : "Select at least one style"}
              </button>

              {/* Help text for multiple styles */}
              {(selectedStylesRef.current || []).length > 1 && (
                <div
                  style={{
                    marginTop: "8px",
                    padding: "6px 10px",
                    backgroundColor: "#f0f4ff",
                    borderRadius: "4px",
                    fontSize: "10px",
                    color: "#2b6cb0",
                    border: "1px solid #bee3f8",
                  }}
                >
                  💡 Multiple styles selected. They will be combined into a
                  single transformation step.
                </div>
              )}
            </div>
          )}

          {/* Show existing style operations summary */}
          {phaseName === "styles" &&
            combinedStep?.phases?.styles?.operations?.length > 0 && (
              <div
                style={{
                  marginTop: "8px",
                  borderTop: "1px solid #e2e8f0",
                  paddingTop: "8px",
                }}
              >
                <div
                  style={{
                    fontSize: "11px",
                    color: "#4a5568",
                    fontWeight: "500",
                    marginBottom: "4px",
                  }}
                >
                  Applied style operations:
                </div>
                {combinedStep.phases.styles.operations.map((op, index) => {
                  const isCombined =
                    op.operation === "style_combined" && op.operations;
                  const opsDisplay = isCombined
                    ? op.operations.join(", ")
                    : op.operation;

                  return (
                    <div
                      key={index}
                      style={{
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "center",
                        padding: "4px 8px",
                        backgroundColor: "#f7fafc",
                        borderRadius: "4px",
                        fontSize: "12px",
                        marginBottom: "4px",
                      }}
                    >
                      <span>
                        🎨 {opsDisplay}
                        {op.style_meta?.alignment &&
                          op.style_meta.alignment !== "left" && (
                            <span
                              style={{
                                marginLeft: "6px",
                                fontSize: "10px",
                                color: "#2b6cb0",
                                backgroundColor: "#ebf8ff",
                                padding: "1px 6px",
                                borderRadius: "8px",
                              }}
                            >
                              {op.style_meta.alignment}
                            </span>
                          )}
                        {op.style_meta?.full_row && (
                          <span
                            style={{
                              marginLeft: "4px",
                              fontSize: "10px",
                              color: "#48bb78",
                            }}
                          >
                            📏
                          </span>
                        )}
                      </span>
                      <button
                        onClick={() =>
                          onRemoveOperationFromPhase("styles", index)
                        }
                        style={{
                          background: "transparent",
                          border: "none",
                          color: "#fc8181",
                          cursor: "pointer",
                          fontSize: "12px",
                        }}
                      >
                        ✕
                      </button>
                    </div>
                  );
                })}
              </div>
            )}
        </div>
      );
    };

    const renderPhaseInfoBubble = () => {
      if (!showPhaseWizard) return null;
      if (!phaseInfoBubbleVisible) return null;

      const msg = PHASE_MESSAGES[currentPhase] || PHASE_MESSAGES[0];

      const isOpening = phaseInfoBubbleState === "opening";
      const isClosing = phaseInfoBubbleState === "closing";
      const isOpen = phaseInfoBubbleState === "open";

      return (
        <div
          style={{
            position: "absolute",
            top: "calc(100% + 12px)",
            left: "50%",
            transform: `translateX(-50%) ${
              isOpen || isOpening
                ? "translateY(0) scale(1)"
                : "translateY(-6px) scale(0.85)"
            }`,
            transformOrigin: "top center",
            opacity: isClosing ? 0 : 1,
            transition: isOpening
              ? "opacity 240ms ease-out, transform 260ms cubic-bezier(0.34, 1.56, 0.64, 1)"
              : isClosing
                ? "opacity 200ms ease-in, transform 220ms cubic-bezier(0.4, 0, 1, 1)"
                : "opacity 200ms ease, transform 200ms ease",
            pointerEvents: isOpen ? "auto" : "none",
            zIndex: 3000,
            width: "440px",
            maxWidth: "calc(100vw - 40px)",
          }}
          aria-hidden={!isOpen}
        >
          {/* Arrow / tail pointing at the info icon */}
          <div
            style={{
              position: "absolute",
              top: "-6px",
              left: "50%",
              transform: "translateX(-50%) rotate(45deg)",
              width: "12px",
              height: "12px",
              backgroundColor: "white",
              borderLeft: `2px solid ${msg.color}`,
              borderTop: `2px solid ${msg.color}`,
              borderRadius: "2px",
              opacity: isClosing ? 0 : 1,
              transition: isOpening
                ? "opacity 260ms ease-out"
                : "opacity 220ms ease-in",
            }}
          />

          <div
            style={{
              backgroundColor: "white",
              border: `2px solid ${msg.color}`,
              borderRadius: "14px",
              padding: "16px 18px 14px",
              boxShadow: `0 16px 40px ${msg.color}30, 0 6px 16px rgba(0,0,0,0.08)`,
              fontFamily:
                '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
              position: "relative",
            }}
          >
            {/* ─── Header ─────────────────────────────────────── */}
            <div
              style={{
                display: "flex",
                alignItems: "flex-start",
                justifyContent: "space-between",
                gap: "10px",
                marginBottom: "4px",
              }}
            >
              <div
                style={{ display: "flex", alignItems: "center", gap: "8px" }}
              >
                <span
                  style={{
                    fontSize: "20px",
                    lineHeight: 1,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    width: "28px",
                    height: "28px",
                    borderRadius: "8px",
                    backgroundColor: `${msg.color}18`,
                  }}
                >
                  {msg.icon}
                </span>
                <div style={{ display: "flex", flexDirection: "column" }}>
                  <span
                    style={{
                      fontSize: "15px",
                      fontWeight: "700",
                      color: msg.color,
                      lineHeight: 1.2,
                    }}
                  >
                    {msg.title}
                  </span>
                  <span
                    style={{
                      fontSize: "9px",
                      fontWeight: "600",
                      color: "#a0aec0",
                      letterSpacing: "0.4px",
                      textTransform: "uppercase",
                      marginTop: "2px",
                    }}
                  >
                    Step {currentPhase + 1} of 3
                  </span>
                </div>
              </div>

              <button
                onClick={handleTogglePhaseInfo}
                disabled={phaseInfoTransitioningRef.current}
                style={{
                  width: "25px",
                  height: "25px",
                  backgroundColor: "transparent",
                  border: `1px solid ${msg.color}30`,
                  borderRadius: "50%",
                  color: "#718096",
                  cursor: phaseInfoTransitioningRef.current
                    ? "not-allowed"
                    : "pointer",
                  fontSize: "10px",
                  fontWeight: "600",
                  opacity: phaseInfoTransitioningRef.current ? 0.5 : 1,
                  transition: "background-color 0.15s ease",
                  flexShrink: 0,
                  boxShadow: "rgba(0, 0, 0, 0.16) 0px 1px 4px",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                }}
                onMouseEnter={(e) => {
                  if (!phaseInfoTransitioningRef.current) {
                    e.currentTarget.style.backgroundColor = `${msg.color}10`;
                  }
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.backgroundColor = "transparent";
                }}
                title="Hide this hint"
              >
                <IoClose size={18} />
              </button>
            </div>

            {/* ─── Subtitle ───────────────────────────────────── */}
            <div
              style={{
                fontSize: "12px",
                color: "#4a5568",
                lineHeight: "1.5",
                marginBottom: "10px",
                paddingLeft: "36px",
              }}
            >
              {msg.subtitle}
            </div>

            {/* ─── Numbered steps ─────────────────────────────── */}
            <div
              style={{
                display: "flex",
                flexDirection: "column",
                gap: "6px",
                marginBottom: "10px",
              }}
            >
              {msg.steps.map((s) => (
                <div
                  key={s.n}
                  style={{
                    display: "flex",
                    alignItems: "flex-start",
                    gap: "10px",
                    padding: "8px 10px",
                    backgroundColor: `${msg.color}08`,
                    border: `1px solid ${msg.color}18`,
                    borderRadius: "8px",
                  }}
                >
                  <span
                    style={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      flexShrink: 0,
                      width: "20px",
                      height: "20px",
                      borderRadius: "50%",
                      backgroundColor: msg.color,
                      color: "white",
                      fontSize: "10px",
                      fontWeight: "700",
                      lineHeight: 1,
                    }}
                  >
                    {s.n}
                  </span>
                  <span
                    style={{
                      fontSize: "12px",
                      color: "#2d3748",
                      lineHeight: "1.5",
                    }}
                  >
                    {s.text}
                  </span>
                </div>
              ))}
            </div>

            {/* ─── Tip ────────────────────────────────────────── */}
            {msg.tip && (
              <div
                style={{
                  fontSize: "11px",
                  color: "#718096",
                  fontStyle: "italic",
                  lineHeight: "1.5",
                  padding: "6px 10px",
                  backgroundColor: "#f7fafc",
                  borderRadius: "6px",
                  marginBottom: "10px",
                }}
              >
                💡 {msg.tip}
              </div>
            )}

            {/* ─── Arrow / CTA pointing down to the preview ───── */}
            <div
              style={{
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                gap: "6px",
                fontSize: "11px",
                fontWeight: "600",
                color: msg.color,
                padding: "8px 12px",
                border: `2px dashed ${msg.color}40`,
                borderRadius: "8px",
                backgroundColor: `${msg.color}06`,
              }}
            >
              <span>👇</span>
              <span>{msg.arrowLabel}</span>
            </div>
          </div>
        </div>
      );
    };

    // ============================================================
    // RENDER PHASE INDICATOR
    // ============================================================
    // RightPanel.js - Update the renderPhaseIndicator function

    const renderPhaseIndicator = () => {
      if (!showPhaseWizard) return null;

      const isLastPhase = currentPhase === 2;
      const totalOps = getTotalOperations(combinedStep);

      // ✅ Check if there are any image URLs in the current preview
      const hasAnyImageUrls =
        renderedPreview &&
        (renderedPreview.includes('class="value image-url"') ||
          renderedPreview.includes("data-image-url") ||
          renderedPreview.includes(".jpg") ||
          renderedPreview.includes(".png") ||
          renderedPreview.includes(".jpeg") ||
          renderedPreview.includes("images.pexels.com") ||
          renderedPreview.includes("unsplash.com") ||
          renderedPreview.includes("imgur.com"));

      // Phase config with colors and icons
      const phaseConfig = [
        {
          id: "structural",
          label: "Structure",
          icon: "📐",
          color: "#2b6cb0",
          lightColor: "#ebf8ff",
        },
        {
          id: "media",
          label: "Media",
          icon: "🖼️",
          color: "#8b5e3c",
          lightColor: "#faf0e6",
          // ✅ Add disabled state for Media phase
          isDisabled: !hasAnyImageUrls,
          disabledReason:
            "No images found in your data. You can skip this step.",
        },
        {
          id: "styles",
          label: "Style",
          icon: "🎨",
          color: "#38a169",
          lightColor: "#f0fff4",
        },
      ];

      return (
        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: "12px",
            padding: "4px 16px 4px 20px",
            background: "white",
            borderRadius: "32px",
            border: "1px solid #e2e8f0",
            boxShadow: "0 2px 8px rgba(0,0,0,0.04)",
            // ✅ UX: bubble is positioned relative to this container
            position: "relative",
          }}
        >
          {/* Phase Steps */}
          <div style={{ display: "flex", alignItems: "center", gap: "0px" }}>
            {phaseConfig.map((p, i) => {
              const isActive = i === currentPhase;
              const isCompleted = i < currentPhase;
              const isLast = i === phaseConfig.length - 1;
              const hasOps =
                combinedStep?.phases?.[p.id]?.operations?.length > 0;
              const isDisabled = p.isDisabled || false;

              return (
                <React.Fragment key={p.id}>
                  {isActive && (
                    <style>
                      {`
          @keyframes phaseGlow-${p.id} {
        0%, 100% {
          box-shadow:
            0 0 6px ${p.color}50,
            0 0 14px ${p.color}35,
            0 0 22px ${p.color}20;
        }

        50% {
          box-shadow:
            0 0 8px ${p.color}80,
            0 0 18px ${p.color}55,
            0 0 30px ${p.color}30;
        }
      }
        `}
                    </style>
                  )}
                  <div
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: "6px",
                      padding: "5px 12px",
                      borderRadius: "20px",
                      background: isActive
                        ? `linear-gradient(135deg, ${p.color}, ${p.color}dd)`
                        : isCompleted
                          ? p.lightColor
                          : "transparent",
                      color: isActive
                        ? "white"
                        : isCompleted
                          ? p.color
                          : isDisabled
                            ? "#e2e8f0"
                            : "#a0aec0",
                      fontSize: "11px",
                      fontWeight: isActive
                        ? "600"
                        : isCompleted
                          ? "500"
                          : "400",
                      transition: "all 0.3s ease",
                      border: isActive
                        ? "none"
                        : isDisabled
                          ? `1px solid #e2e8f0`
                          : `1px solid ${isCompleted ? p.color : "#e2e8f0"}`,
                      // boxShadow: isActive ? `0 2px 8px ${p.color}30` : "none",
                      boxShadow: isActive
                        ? `0 0 6px ${p.color}50, 0 0 14px ${p.color}35`
                        : "none",

                      animation: isActive
                        ? `phaseGlow-${p.id} 1.8s ease-in-out infinite`
                        : "none",
                      cursor: "default",
                      position: "relative",
                      opacity:
                        isDisabled && !isActive && !isCompleted ? 0.5 : 1,
                      textDecoration:
                        isDisabled && !isActive && !isCompleted
                          ? "line-through"
                          : "none",
                    }}
                    title={
                      isDisabled && !isActive && !isCompleted
                        ? p.disabledReason
                        : ""
                    }
                  >
                    {/* Step Number Badge */}
                    <span
                      style={{
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        width: "18px",
                        height: "18px",
                        borderRadius: "50%",
                        background: isActive
                          ? "rgba(255,255,255,0.2)"
                          : isCompleted
                            ? p.color
                            : isDisabled
                              ? "#e2e8f0"
                              : "#e2e8f0",
                        color: isActive
                          ? "white"
                          : isCompleted
                            ? "white"
                            : isDisabled
                              ? "#a0aec0"
                              : "#a0aec0",
                        fontSize: "9px",
                        fontWeight: "700",
                      }}
                    >
                      {isCompleted ? "✓" : i + 1}
                    </span>

                    <span>{p.icon}</span>
                    <span>{p.label}</span>

                    {/* ============================================================
                        ✅ UX: Info icon — only on the ACTIVE phase chip.
                        Clicking it toggles the phase-info bubble.
                        Disabled while the bubble transition is in flight.
                        ============================================================ */}
                    {isActive && !isDisabled && (
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          handleTogglePhaseInfo();
                        }}
                        disabled={phaseInfoTransitioningRef.current}
                        title="Show phase help"
                        aria-label="Show phase help"
                        style={{
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          width: "16px",
                          height: "16px",
                          marginLeft: "2px",
                          padding: 0,
                          borderRadius: "50%",
                          border: "1px solid rgba(255,255,255,0.5)",
                          backgroundColor:
                            phaseInfoBubbleState === "open" ||
                            phaseInfoBubbleState === "opening"
                              ? "rgba(255,255,255,0.35)"
                              : "rgba(255,255,255,0.15)",
                          color: "white",
                          cursor: phaseInfoTransitioningRef.current
                            ? "not-allowed"
                            : "pointer",
                          fontSize: "10px",
                          fontWeight: "700",
                          fontStyle: "italic",
                          fontFamily: "Georgia, serif",
                          lineHeight: 1,
                          transition: "background-color 0.15s ease",
                          opacity: phaseInfoTransitioningRef.current ? 0.6 : 1,
                        }}
                        onMouseEnter={(e) => {
                          if (!phaseInfoTransitioningRef.current) {
                            e.currentTarget.style.backgroundColor =
                              "rgba(255,255,255,0.4)";
                          }
                        }}
                        onMouseLeave={(e) => {
                          e.currentTarget.style.backgroundColor =
                            phaseInfoBubbleState === "open" ||
                            phaseInfoBubbleState === "opening"
                              ? "rgba(255,255,255,0.35)"
                              : "rgba(255,255,255,0.15)";
                        }}
                      >
                        i
                      </button>
                    )}

                    {/* Operation count badge */}
                    {hasOps && (
                      <span
                        style={{
                          fontSize: "8px",
                          background: isActive
                            ? "rgba(255,255,255,0.2)"
                            : isCompleted
                              ? p.color
                              : "#edf2f7",
                          color: isActive
                            ? "white"
                            : isCompleted
                              ? "white"
                              : "#718096",
                          padding: "1px 7px",
                          borderRadius: "10px",
                          fontWeight: "600",
                        }}
                      >
                        {combinedStep?.phases?.[p.id]?.operations?.length}
                      </span>
                    )}

                    {/* ✅ Show disabled icon for Media phase with no images */}
                    {isDisabled && !isActive && !isCompleted && (
                      <span
                        style={{
                          fontSize: "10px",
                          marginLeft: "2px",
                          opacity: 0.7,
                        }}
                      >
                        ⛔
                      </span>
                    )}

                    {/* Active indicator dot */}
                    {isActive && (
                      <span
                        style={{
                          position: "absolute",
                          bottom: "-3px",
                          left: "50%",
                          transform: "translateX(-50%)",
                          width: "6px",
                          height: "6px",
                          borderRadius: "50%",
                          background: p.color,
                          boxShadow: `0 0 8px ${p.color}60`,
                        }}
                      />
                    )}
                  </div>

                  {/* Connector line between steps */}
                  {!isLast && (
                    <div
                      style={{
                        width: "20px",
                        height: "2px",
                        background: isCompleted
                          ? phaseConfig[i].color
                          : "#e2e8f0",
                        borderRadius: "2px",
                        opacity: isCompleted ? 0.5 : 0.3,
                        margin: "0 2px",
                      }}
                    />
                  )}
                </React.Fragment>
              );
            })}
          </div>

          {/* Divider */}
          <div
            style={{
              width: "1px",
              height: "24px",
              background: "#e2e8f0",
              margin: "0 4px",
            }}
          />

          {/* Ops Counter */}
          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: "4px",
              fontSize: "10px",
              color: "#4a5568",
              fontWeight: "500",
            }}
          >
            <span style={{ fontSize: "12px" }}>📊</span>
            <span>
              {totalOps} {totalOps === 1 ? "op" : "ops"}
            </span>
          </div>

          {/* Action Button */}
          {!isLastPhase ? (
            <button
              onClick={handleNextPhaseWithAutoClose}
              style={{
                padding: "5px 16px",
                background: "linear-gradient(135deg, #4a7feb, #3b6bd4)",
                color: "white",
                border: "none",
                borderRadius: "20px",
                cursor: "pointer",
                fontSize: "11px",
                fontWeight: "600",
                display: "flex",
                alignItems: "center",
                gap: "6px",
                transition: "all 0.3s ease",
                boxShadow: "0 2px 8px rgba(74, 127, 235, 0.25)",
              }}
              onMouseEnter={(e) => {
                e.target.style.transform = "translateY(-1px)";
                e.target.style.boxShadow =
                  "0 4px 16px rgba(74, 127, 235, 0.35)";
              }}
              onMouseLeave={(e) => {
                e.target.style.transform = "translateY(0)";
                e.target.style.boxShadow = "0 2px 8px rgba(74, 127, 235, 0.25)";
              }}
            >
              Next Step
              <span style={{ fontSize: "12px" }}>→</span>
            </button>
          ) : (
            <button
              onClick={handleCompleteWithAutoClose}
              disabled={totalOps === 0}
              style={{
                padding: "5px 16px",
                background:
                  totalOps > 0
                    ? "linear-gradient(135deg, #48bb78, #38a169)"
                    : "#e2e8f0",
                color: totalOps > 0 ? "white" : "#a0aec0",
                border: "none",
                borderRadius: "20px",
                cursor: totalOps > 0 ? "pointer" : "not-allowed",
                fontSize: "11px",
                fontWeight: "600",
                display: "flex",
                alignItems: "center",
                gap: "6px",
                transition: "all 0.3s ease",
                boxShadow:
                  totalOps > 0 ? "0 2px 8px rgba(72, 187, 120, 0.25)" : "none",
              }}
              onMouseEnter={(e) => {
                if (totalOps > 0) {
                  e.target.style.transform = "translateY(-1px)";
                  e.target.style.boxShadow =
                    "0 4px 16px rgba(72, 187, 120, 0.35)";
                }
              }}
              onMouseLeave={(e) => {
                if (totalOps > 0) {
                  e.target.style.transform = "translateY(0)";
                  e.target.style.boxShadow =
                    "0 2px 8px rgba(72, 187, 120, 0.25)";
                }
              }}
            >
              <span>✓</span>
              Complete
            </button>
          )}

          {/* ============================================================
              ✅ UX: Phase info bubble — anchored to the active chip.
              Rendered inside the relative-positioned wrapper so it
              grows out of the correct info icon on open, and shrinks
              back into it on close.
              ============================================================ */}
          {renderPhaseInfoBubble()}
        </div>
      );
    };

    // ============================================================
    // RENDER "OPEN EDITOR" BUTTON
    // ============================================================
    const renderOpenEditorButton = () => {
      const isDisabled = showPhaseWizard || !hasPreviewData;

      return (
        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: "8px",
            padding: "4px 0",
            flexWrap: "wrap",
            opacity: isDisabled ? 0.5 : 1,
            pointerEvents: isDisabled ? "none" : "auto",
          }}
        >
          <button
            id="edit-template-btn"
            onClick={handleToggleEditMode}
            disabled={isDisabled}
            style={{
              padding: "4px 12px",
              backgroundColor: isEditMode
                ? "#fc8181"
                : isDisabled
                  ? "#a0aec0"
                  : "#4299e1",
              color: "white",
              border: "none",
              borderRadius: "12px",
              cursor: isDisabled ? "not-allowed" : "pointer",
              fontSize: "11px",
              fontWeight: "500",
              transition: "all 0.2s",
              opacity: isDisabled ? 0.6 : 1,
            }}
            // title={
            //   isDisabled
            //     ? showPhaseWizard
            //       ? "✏️ Close wizard first"
            //       : "⏳ Please render a preview first"
            //     : ""
            // }
            title={
              isDisabled
                ? showPhaseWizard
                  ? "✏️ Close wizard first"
                  : "⏳ Please render a preview first"
                : isEditMode
                  ? "Close the editor"
                  : "After clicking on this button you can customize your template"
            }
            className={!isEditMode && !isDisabled ? styles.editBtnGlow : ""}
          >
            {isEditMode ? "✕ Close Editor" : "✏️ Open Editor"}
          </button>

          {isEditMode && !isDisabled && (
            <>
              <span
                style={{
                  fontSize: "11px",
                  color: "#718096",
                  padding: "2px 8px",
                  backgroundColor: "#f7fafc",
                  borderRadius: "10px",
                }}
              >
                {templateLines.length} rows
              </span>
              {hasTemplateChanges && (
                <span
                  style={{
                    fontSize: "10px",
                    color: "#e53e3e",
                    fontWeight: "600",
                  }}
                >
                  • Unsaved changes (auto-applied)
                </span>
              )}
              <button
                id="custom-message-btn"
                onClick={handleAddCustomMessage}
                style={{
                  padding: "4px 10px",
                  backgroundColor: "#9f7aea",
                  color: "white",
                  border: "none",
                  borderRadius: "12px",
                  cursor: "pointer",
                  fontSize: "11px",
                  fontWeight: "500",
                }}
              >
                + Custom Message
              </button>
              {hasTemplateChanges && (
                <button
                  onClick={handleDiscardChanges}
                  style={{
                    padding: "4px 10px",
                    backgroundColor: "#fc8181",
                    color: "white",
                    border: "none",
                    borderRadius: "12px",
                    cursor: "pointer",
                    fontSize: "11px",
                    fontWeight: "500",
                  }}
                >
                  ↩ Revert Changes
                </button>
              )}
            </>
          )}
        </div>
      );
    };

    // ============================================================
    // RENDER WARNING MODAL
    // ============================================================
    const renderWarningModal = () => {
      if (!showCloseEditorWarning) return null;

      const isWizardClosing = !pendingWizardStart;
      const title = isWizardClosing ? "Close Wizard?" : "Close Editor?";
      const message = isWizardClosing
        ? "You have transformations applied in the wizard. Closing it will discard them. Continue?"
        : "You have unsaved changes in the editor. These changes will be auto-applied when you close. Continue?";

      return (
        <div
          style={{
            position: "fixed",
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: "rgba(0,0,0,0.5)",
            zIndex: 9999,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            backdropFilter: "blur(4px)",
            animation: "fadeIn 0.3s ease",
          }}
        >
          <style>{`@keyframes fadeIn { from { opacity: 0; transform: scale(0.95); } to { opacity: 1; transform: scale(1); } }`}</style>
          <div
            style={{
              backgroundColor: "white",
              borderRadius: "16px",
              padding: "32px 40px",
              maxWidth: "480px",
              width: "90%",
              boxShadow: "0 20px 60px rgba(0,0,0,0.3)",
            }}
          >
            <div
              style={{
                fontSize: "48px",
                textAlign: "center",
                marginBottom: "12px",
              }}
            >
              ⚠️
            </div>
            <h2
              style={{
                fontSize: "22px",
                fontWeight: "700",
                color: "#2d3748",
                textAlign: "center",
                margin: "0 0 8px 0",
              }}
            >
              {title}
            </h2>
            <p
              style={{
                fontSize: "15px",
                color: "#4a5568",
                textAlign: "center",
                lineHeight: "1.6",
                margin: "0 0 20px 0",
              }}
            >
              {message}
            </p>
            <div
              style={{ display: "flex", gap: "10px", justifyContent: "center" }}
            >
              <button
                onClick={() => {
                  setShowCloseEditorWarning(false);
                  setPendingWizardStart(false);
                }}
                style={{
                  padding: "10px 24px",
                  backgroundColor: "transparent",
                  color: "#4a5568",
                  border: "1px solid #e2e8f0",
                  borderRadius: "8px",
                  cursor: "pointer",
                  fontSize: "14px",
                  fontWeight: "500",
                  flex: 1,
                }}
              >
                Cancel
              </button>
              <button
                onClick={() => {
                  setShowCloseEditorWarning(false);
                  if (isWizardClosing) {
                    if (onCancelWizard) onCancelWizard();
                    if (onCloseTransformationPanel)
                      onCloseTransformationPanel();
                    if (setIsSelectingMode) setIsSelectingMode(false);
                    setIsEditMode(true);
                  } else {
                    setIsEditMode(false);
                    setHasTemplateChanges(false);
                    setOriginalTemplate(template);
                    if (setTemplateLines) setTemplateLines([]);
                    setIsSelectingMode(true);
                    if (onStartPhaseWizard) onStartPhaseWizard();
                  }
                  setPendingWizardStart(false);
                }}
                style={{
                  padding: "10px 32px",
                  backgroundColor: "#e53e3e",
                  color: "white",
                  border: "none",
                  borderRadius: "8px",
                  cursor: "pointer",
                  fontSize: "14px",
                  fontWeight: "600",
                  flex: 1,
                }}
              >
                {isWizardClosing ? "Close Wizard" : "Close & Continue"}
              </button>
            </div>
          </div>
        </div>
      );
    };

    const safeTemplate = typeof template === "string" ? template : "";

    // ============================================================
    // FIX: Start Wizard handler with empty selector check
    // ============================================================
    const handleStartWizard = () => {
      if (isEditMode && hasTemplateChanges) {
        setShowCloseEditorWarning(true);
        setPendingWizardStart(true);
        return;
      }

      if (isEditMode) {
        setIsEditMode(false);
        setHasTemplateChanges(false);
        setOriginalTemplate(template);
        if (setTemplateLines) setTemplateLines([]);
      }

      setIsSelectingMode(true);

      // Call onStartPhaseWizard with a flag to indicate no text selected
      // The parent component should handle this by creating an empty combined step
      if (onStartPhaseWizard) {
        onStartPhaseWizard(false); // Pass false to indicate no text selected
      }
    };

    // Get the case type for Controls
    const detectedCaseType = detectCaseType();

    return (
      <div
        ref={containerRef}
        className={styles.rightPanel}
        style={{ position: "relative" }}
      >
        <div className={styles.previewHeader}>
          <h3 className={styles.previewTitle}>📄 Final Preview</h3>
          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: "12px",
              flexWrap: "wrap",
              justifyContent: "space-between",
              width: "100%",
            }}
          >
            {/* ============================================================
        LEFT GROUP — info chip, editor button, phase indicator,
        wizard actions. Takes as much room as it needs.
        ============================================================ */}
            <div
              style={{
                display: "flex",
                alignItems: "center",
                gap: "12px",
                flexWrap: "wrap",
                minWidth: 0,
                flex: "1 1 auto",
              }}
            >
              <div
                style={{
                  fontSize: "11px",
                  color: "#718096",
                  backgroundColor: "#edf2f7",
                  padding: "2px 10px",
                  borderRadius: "12px",
                  display: "flex",
                  alignItems: "center",
                  gap: "8px",
                  flexShrink: 0,
                }}
              >
                <span>
                  Type: <strong>{dataType || "unknown"}</strong>
                </span>

                {detectedCaseType &&
                  detectedCaseType !== "unknown" &&
                  (() => {
                    const caseInfo = getCaseTypeLabel(detectedCaseType);
                    return (
                      <span
                        style={{
                          display: "flex",
                          alignItems: "center",
                          gap: "4px",
                          marginLeft: "4px",
                          padding: "1px 10px",
                          borderRadius: "10px",
                          fontSize: "9px",
                          fontWeight: "600",
                          backgroundColor: caseInfo.bgColor,
                          color: caseInfo.color,
                          border: `1px solid ${caseInfo.color}30`,
                          position: "relative",
                          cursor: "default",
                        }}
                      >
                        <span>{caseInfo.icon}</span>
                        <span>{caseInfo.label}</span>

                        {/* Info Icon with Tooltip */}
                        <span
                          style={{
                            display: "inline-flex",
                            alignItems: "center",
                            justifyContent: "center",
                            width: "14px",
                            height: "14px",
                            borderRadius: "50%",
                            backgroundColor: `${caseInfo.color}20`,
                            color: caseInfo.color,
                            fontSize: "8px",
                            fontWeight: "700",
                            cursor: "help",
                            marginLeft: "2px",
                            transition: "all 0.2s",
                          }}
                          onMouseEnter={(e) => {
                            e.target.style.backgroundColor = caseInfo.color;
                            e.target.style.color = "white";
                            const tooltip = e.target.nextElementSibling;
                            if (tooltip) {
                              tooltip.style.opacity = "1";
                              tooltip.style.visibility = "visible";
                              tooltip.style.transform =
                                "translateX(-50%) translateY(0)";
                            }
                          }}
                          onMouseLeave={(e) => {
                            e.target.style.backgroundColor = `${caseInfo.color}20`;
                            e.target.style.color = caseInfo.color;
                            const tooltip = e.target.nextElementSibling;
                            if (tooltip) {
                              tooltip.style.opacity = "0";
                              tooltip.style.visibility = "hidden";
                              tooltip.style.transform =
                                "translateX(-50%) translateY(-5px)";
                            }
                          }}
                        >
                          i
                        </span>

                        {/* Tooltip */}
                        <span
                          style={{
                            position: "absolute",
                            bottom: "calc(100% + 8px)",
                            left: "50%",
                            transform: "translateX(-50%) translateY(-5px)",
                            backgroundColor: "#1a202c",
                            color: "white",
                            fontSize: "10px",
                            fontWeight: "400",
                            padding: "4px 10px",
                            borderRadius: "6px",
                            whiteSpace: "nowrap",
                            opacity: 0,
                            visibility: "hidden",
                            transition: "all 0.2s ease",
                            boxShadow: "0 4px 12px rgba(0,0,0,0.15)",
                            pointerEvents: "none",
                            zIndex: 100,
                            fontFamily:
                              '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
                          }}
                        >
                          {caseInfo.description}
                          <span
                            style={{
                              position: "absolute",
                              bottom: "-4px",
                              left: "50%",
                              transform: "translateX(-50%) rotate(45deg)",
                              width: "8px",
                              height: "8px",
                              backgroundColor: "#1a202c",
                            }}
                          />
                        </span>
                      </span>
                    );
                  })()}
              </div>

              {renderOpenEditorButton()}

              <div
                style={{ display: "flex", alignItems: "center", gap: "8px" }}
              >
                {hasPreviewData && !showPhaseWizard && (
                  <button
                    id="start-wizard-btn"
                    onClick={handleStartWizard}
                    style={{
                      padding: "4px 14px",
                      backgroundColor:
                        isEditMode || !hasPreviewData ? "#a0aec0" : "#48bb78",
                      color: "white",
                      border: "none",
                      borderRadius: "12px",
                      cursor:
                        isEditMode || !hasPreviewData
                          ? "not-allowed"
                          : "pointer",
                      fontSize: "11px",
                      fontWeight: "600",
                      display: "flex",
                      alignItems: "center",
                      gap: "4px",
                      opacity: isEditMode || !hasPreviewData ? 0.6 : 1,
                    }}
                    className={
                      !isEditMode && hasPreviewData ? styles.wizardBtnGlow : ""
                    }
                    title="After clicking here you can apply Transformations in you card"
                  >
                    🚀 Start Wizard
                  </button>
                )}

                {showPhaseWizard && (
                  <button
                    onClick={() => {
                      if (onCancelWizard) onCancelWizard();
                      if (setIsSelectingMode) setIsSelectingMode(false);
                      if (onCloseTransformationPanel)
                        onCloseTransformationPanel();
                    }}
                    title="Close Your Wizard"
                    style={{
                      padding: "4px 14px",
                      backgroundColor: "#fc8181",
                      color: "white",
                      border: "none",
                      borderRadius: "50%",
                      cursor: "pointer",
                      fontSize: "18px",
                      fontWeight: "600",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      gap: "4px",
                      width: "30px",
                      height: "30px",
                    }}
                  >
                    ✕
                  </button>
                )}

                {renderPhaseIndicator()}

                {showPhaseWizard && selectedText && selectionInfo && (
                  <span
                    style={{
                      fontSize: "9px",
                      color: "#4a5568",
                      backgroundColor: "#edf2f7",
                      padding: "1px 8px",
                      borderRadius: "10px",
                      display: "flex",
                      alignItems: "center",
                      gap: "3px",
                    }}
                  >
                    {selectionInfo.icon} {selectionInfo.label}
                  </span>
                )}
              </div>
            </div>

            {/* ============================================================
        RIGHT GROUP — Desktop / Mobile view toggle.
        `marginLeft: auto` keeps it glued to the far right even
        when the left group wraps to a second row.
        ============================================================ */}
            <div
              style={{
                display: "flex",
                alignItems: "center",
                gap: "4px",
                backgroundColor: "#f1f4f9",
                borderRadius: "20px",
                padding: "3px",
                boxShadow: "inset 0 1px 3px rgba(0,0,0,0.06)",
                marginLeft: "auto",
                flexShrink: 0,
              }}
            >
              <button
                onClick={() => setViewMode("desktop")}
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: "4px",
                  padding: "4px 12px",
                  borderRadius: "16px",
                  border: "none",
                  cursor: "pointer",
                  fontSize: "10px",
                  fontWeight: viewMode === "desktop" ? "600" : "500",
                  backgroundColor:
                    viewMode === "desktop" ? "white" : "transparent",
                  color: viewMode === "desktop" ? "#1a202c" : "#94a3b8",
                  boxShadow:
                    viewMode === "desktop"
                      ? "0 1px 4px rgba(0,0,0,0.08)"
                      : "none",
                  transition: "all 0.25s ease",
                  fontFamily:
                    '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
                }}
                onMouseEnter={(e) => {
                  if (viewMode !== "desktop") {
                    e.currentTarget.style.color = "#4a5568";
                    e.currentTarget.style.backgroundColor =
                      "rgba(255,255,255,0.5)";
                  }
                }}
                onMouseLeave={(e) => {
                  if (viewMode !== "desktop") {
                    e.currentTarget.style.color = "#94a3b8";
                    e.currentTarget.style.backgroundColor = "transparent";
                  }
                }}
                title="Desktop view — full width"
              >
                <span style={{ fontSize: "12px" }}>🖥️</span>
                Desktop
              </button>

              <span
                style={{
                  color: "#e2e8f0",
                  fontSize: "10px",
                  fontWeight: "300",
                }}
              >
                |
              </span>

              <button
                onClick={() => setViewMode("mobile")}
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: "4px",
                  padding: "4px 12px",
                  borderRadius: "16px",
                  border: "none",
                  cursor: "pointer",
                  fontSize: "10px",
                  fontWeight: viewMode === "mobile" ? "600" : "500",
                  backgroundColor:
                    viewMode === "mobile" ? "white" : "transparent",
                  color: viewMode === "mobile" ? "#1a202c" : "#94a3b8",
                  boxShadow:
                    viewMode === "mobile"
                      ? "0 1px 4px rgba(0,0,0,0.08)"
                      : "none",
                  transition: "all 0.25s ease",
                  fontFamily:
                    '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
                }}
                onMouseEnter={(e) => {
                  if (viewMode !== "mobile") {
                    e.currentTarget.style.color = "#4a5568";
                    e.currentTarget.style.backgroundColor =
                      "rgba(255,255,255,0.5)";
                  }
                }}
                onMouseLeave={(e) => {
                  if (viewMode !== "mobile") {
                    e.currentTarget.style.color = "#94a3b8";
                    e.currentTarget.style.backgroundColor = "transparent";
                  }
                }}
                title="Mobile view — 390px width"
              >
                <span style={{ fontSize: "12px" }}>📱</span>
                Mobile
              </button>
            </div>
          </div>
        </div>

        <PreviewContainer
          id="preview-container"
          getScrollStyle={getScrollStyle}
          getPreviewContent={getPreviewContentWithLogging}
          viewMode={isEditMode ? "edit" : "default"}
          onTextSelect={onTextSelect}
          isSelectingMode={showPhaseWizard}
          previewViewMode={viewMode}
          currentPhase={currentPhase}
          showTransformationPanel={showTransformationPanel}
        />

        {renderedPreview && !error && (
          <PreviewInfo
            dataType={dataType}
            renderedPreview={renderedPreview}
            parsedData={parsedData}
          />
        )}

        {scenarioTree.length > 0 && !error && (
          <ScenarioTree
            scenarioTree={scenarioTree}
            renderScenarioTree={renderScenarioTree}
          />
        )}

        <div id="transformation-popup">{renderTransformationPopup()}</div>

        <button
          onClick={() => {
            setIsControlsOpen(!isControlsOpen);
          }}
          ref={controlsToggleRef}
          style={{
            position: "fixed",
            right: isControlsOpen ? "260px" : "0px",
            top: "50%",
            transform: "translateY(-50%)",
            zIndex: 1001,
            backgroundColor: "#4299e1",
            color: "white",
            border: "none",
            borderRadius: "4px 0 0 4px",
            padding: "10px 6px",
            cursor: "pointer",
            fontSize: "16px",
            writingMode: "vertical-lr",
            letterSpacing: "1px",
            transition: "right 0.3s ease",
          }}
        >
          {isControlsOpen ? "◀ Controls" : "Controls ▶"}
        </button>

        <div
          ref={controlsPanelRef}
          style={{
            position: "fixed",
            right: isControlsOpen ? "0px" : "-280px",
            top: "0",
            width: "280px",
            height: "100vh",
            backgroundColor: "white",
            boxShadow: "-2px 0 10px rgba(0,0,0,0.1)",
            zIndex: 1000,
            padding: "16px",
            overflowY: "auto",
            transition: "right 0.3s ease",
            borderLeft: "1px solid #e2e8f0",
          }}
        >
          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              marginBottom: "16px",
            }}
          >
            <h4 style={{ margin: 0, color: "#2d3748", fontSize: "14px" }}>
              ⚙️ Controls
            </h4>
            <button
              onClick={() => setIsControlsOpen(false)}
              style={{
                backgroundColor: "transparent",
                border: "1px solid #e2e8f0",
                borderRadius: "4px",
                padding: "2px 8px",
                cursor: "pointer",
                fontSize: "14px",
                color: "#718096",
              }}
            >
              ✕
            </button>
          </div>
          {renderedPreview && !error ? (
            <Controls
              scrollMode={scrollMode}
              setScrollMode={setScrollMode}
              layoutDirection={layoutDirection}
              setLayoutDirection={setLayoutDirection}
              dataType={dataType}
              isSampleArray={isSampleArray}
              arrayLayoutDirection={arrayLayoutDirection}
              setArrayLayoutDirection={setArrayLayoutDirection}
              hasParsedData={hasParsedData}
              template={safeTemplate}
              configId={configId}
              kbId={kbId}
              statusCode={statusCode}
              onSaveSuccess={handleControlsSaveSuccess}
              actions={actions}
              onAddAction={onAddAction}
              onRemoveAction={onRemoveAction}
              transformationRules={transformationRules}
              // ✅ Pass caseType to Controls
              caseType={detectedCaseType}
              // Modal mode props
              modalMode={modalMode}
              onTemplateDataReady={onTemplateDataReady}
              parsedSample={parsedSample}
              templateString={safeTemplate}
              buttons={buttons}
              queryId={queryId}
              onUpdateAction={handleUpdateAction}
              templateLines={templateLines}
            />
          ) : (
            <div
              style={{
                padding: "20px",
                textAlign: "center",
                color: "#a0aec0",
                fontSize: "13px",
              }}
            >
              {error ? "⚠️ Error in preview" : "No preview to control"}
            </div>
          )}
        </div>

        {renderWarningModal()}

        <style>{`
          @keyframes slideUp { from { opacity: 0; transform: translateX(-50%) translateY(20px); } to { opacity: 1; transform: translateX(-50%) translateY(0); } }
          @keyframes pulse-blue { 0% { opacity: 0.6; } 50% { opacity: 1; } 100% { opacity: 0.6; } }
        `}</style>
      </div>
    );
  },
);

RightPanel.displayName = "RightPanel";

export default RightPanel;
